import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import {
  IApiResponseProjetistas,
  IProjetista,
  ISalvarProjetista,
  ILinha,
  IUploadPacoteLinhaResponse,
  IUploadArquivoComumResponse,
  IReferenciaArquivoComum,
} from "shared/interfaces/projetista";

export const getProjetistas = async (params?: any): Promise<IProjetista[]> => {
  try {
    const { data } = await api.get<IApiResponseProjetistas>("/Projetista/ListarProjetistas", {
      params: {
        sortBy: params?.sortBy,
        page: params?.page,
        limit: params?.pageSize,
        direction: params?.direction,
        filtrar: params?.search || "",
      },
    });
    return data.items ?? [];
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar os Projetistas";
    getErrorsByApi(error, errorMessage);
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Projetistas configurados no Cadastros N2 da PRÓPRIA empresa do usuário logado (a empresa vem do
 * token). Plano de 22/09/2026, Frente 2 — mesma razão de getMeusCadastrosN2: o
 * `Projetista/ListarProjetistas` exige a tela Configuracao (Gerência) e devolve 403 para o usuário
 * de Cadastros N2, deixando filtros e seletores vazios. As telas de Gerência seguem em
 * `getProjetistas`, que lista todos.
 *
 * Diferente de getProjetistas, propaga o erro: quem chama precisa distinguir "não consegui ler" de
 * "a empresa não configurou projetista nenhum" (Frente 3).
 */
export const getMeusProjetistas = async (): Promise<IProjetista[]> => {
  const { data } = await api.get<IProjetista[]>("/MinhaEmpresa/ListarMeusProjetistas");
  return data ?? [];
};

export const getDadosPaginacao = async (params?: any): Promise<number> => {
  try {
    const { data } = await api.get<IApiResponseProjetistas>("/Projetista/ListarProjetistas", {
      params: {
        sortBy: params?.sortBy,
        page: params?.page,
        limit: params?.pageSize,
        direction: params?.direction,
        filtrar: params?.search || "",
      },
    });
    return data.totalRecords ?? 0;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar paginação";
    getErrorsByApi(error, errorMessage);
    return 0;
  }
};

export const getProjetistaPorId = async (idProjetista: number): Promise<IProjetista | null> => {
  if (!idProjetista) throw new Error("Id do Projetista não informado");
  try {
    const { data } = await api.get<IProjetista>(
      `/Projetista/ObterProjetistaPorId?idProjetista=${idProjetista}`
    );
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar o Projetista";
    getErrorsByApi(error, errorMessage);
    return null;
  }
};

/**
 * Referencias oferecidas no campo "Usar o arquivo comum na referencia" (P6 de 17/08/2026).
 * As ja vinculadas a OUTRO projetista nao vem na lista — a exclusividade e regra do PO.
 */
export const getReferenciasArquivoComum = async (
  idProjetista?: number
): Promise<IReferenciaArquivoComum[]> => {
  try {
    const { data } = await api.get<IReferenciaArquivoComum[]>(
      "/Projetista/ListarReferenciasArquivoComum",
      { params: { idProjetista } }
    );
    return data ?? [];
  } catch (error: any) {
    getErrorsByApi(error, "Tivemos problemas internos ao buscar as Referências");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

export const getLinhasSistemas = async (): Promise<ILinha[]> => {
  try {
    const { data } = await api.get<ILinha[]>("/Projetista/ListarLinhasSistemas");
    return data ?? [];
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar Linhas/Sistemas";
    getErrorsByApi(error, errorMessage);
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

export const postProjetista = async (projetista: ISalvarProjetista): Promise<number | null> => {
  try {
    const { data } = await api.post<number>("/Projetista/SalvarProjetista", projetista);
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao salvar o Projetista";
    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const deleteProjetista = async (idProjetista: number): Promise<boolean> => {
  if (!idProjetista) throw new Error("Id do Projetista não informado");
  try {
    await api.delete(`/Projetista/ExcluirProjetista?idProjetista=${idProjetista}`);
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao excluir o Projetista";
    getErrorsByApi(error, errorMessage);
    return false;
  }
};

export const postAtivarDesativarProjetista = async (
  idProjetista: number,
  ativar: boolean
): Promise<boolean> => {
  if (!idProjetista) throw new Error("Id do Projetista não informado");
  try {
    await api.post(`/Projetista/AtivarDesativar?idProjetista=${idProjetista}&ativar=${ativar}`);
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao alterar status do Projetista";
    getErrorsByApi(error, errorMessage);
    return false;
  }
};

export const uploadLogoProjetista = async (file: File): Promise<string | null> => {
  if (!file) throw new Error("Arquivo não fornecido");
  
  try {
    const formData = new FormData();
    formData.append("file", file);
    
    const { data } = await api.post<{ url: string }>("/Projetista/UploadLogo", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    
    return data.url;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao fazer upload do logo";
    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const uploadPacoteLinha = async (
  file: File,
  projetistaLinhaId: number,
  disponivelCemPro: boolean = true,
  disponivelCemMobi: boolean = false
): Promise<IUploadPacoteLinhaResponse | null> => {
  if (!file) throw new Error("Arquivo não fornecido");
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("projetistaLinhaId", projetistaLinhaId.toString());
    formData.append("disponivelCemPro", disponivelCemPro.toString());
    formData.append("disponivelCemMobi", disponivelCemMobi.toString());

    const { data } = await api.post<IUploadPacoteLinhaResponse>(
      "/Projetista/UploadPacoteLinha",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao fazer upload do pacote";
    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const obterPacoteLinha = async (
  projetistaLinhaId: number
): Promise<IUploadPacoteLinhaResponse | null> => {
  try {
    const { data } = await api.get<IUploadPacoteLinhaResponse>(
      `/Projetista/ObterPacoteLinha?projetistaLinhaId=${projetistaLinhaId}`
    );
    return data;
  } catch (error: any) {
    return null;
  }
};

export const removerPacoteLinha = async (
  projetistaLinhaId: number
): Promise<boolean> => {
  try {
    await api.delete(
      `/Projetista/RemoverPacoteLinha?projetistaLinhaId=${projetistaLinhaId}`
    );
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao remover o pacote";
    getErrorsByApi(error, errorMessage);
    return false;
  }
};

export const downloadPacoteLinha = async (
  projetistaLinhaId: number
): Promise<void> => {
  try {
    const response = await api.get(
      `/Projetista/DownloadPacoteLinha?projetistaLinhaId=${projetistaLinhaId}`,
      { responseType: "blob" }
    );
    const blob = new Blob([response.data], { type: "application/xml" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "pacote.xml";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        fileName = match[1].replace(/['"]/g, "");
      }
    }
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao baixar o pacote";
    getErrorsByApi(error, errorMessage);
  }
};

export const uploadArquivoComum = async (
  file: File,
  idProjetista: number
): Promise<IUploadArquivoComumResponse | null> => {
  if (!file) throw new Error("Arquivo não fornecido");
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("idProjetista", idProjetista.toString());

    const { data } = await api.post<IUploadArquivoComumResponse>(
      "/Projetista/UploadArquivoComum",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao fazer upload do Arquivo Comum";
    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const removerArquivoComum = async (
  idProjetista: number
): Promise<boolean> => {
  try {
    await api.delete(
      `/Projetista/RemoverArquivoComum?idProjetista=${idProjetista}`
    );
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao remover o Arquivo Comum";
    getErrorsByApi(error, errorMessage);
    return false;
  }
};

export const downloadArquivoComum = async (
  idProjetista: number
): Promise<void> => {
  try {
    const response = await api.get(
      `/Projetista/DownloadArquivoComum?idProjetista=${idProjetista}`,
      { responseType: "blob" }
    );
    const blob = new Blob([response.data], { type: "application/zip" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const contentDisposition = response.headers["content-disposition"];
    let fileName = "arquivo-comum.zip";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        fileName = match[1].replace(/['"]/g, "");
      }
    }
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao baixar o Arquivo Comum";
    getErrorsByApi(error, errorMessage);
  }
};














